import { Injectable, inject } from '@angular/core';
import { CanActivate, CanActivateFn, Router, UrlTree } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { content } from 'src/environments/data';
import { User } from './shared/interfaces/user.interface';

/**
 * Hard-stonewall incomplete voice signup users (signup_complete === false)
 * onto /signup/{role}, allowing only signup/login/signout/password routes.
 */
@Injectable({ providedIn: 'root' })
export class SignupIncompleteGuard implements CanActivate {
  constructor(
    private cookieService: CookieService,
    private router: Router
  ) {}

  canActivate(): boolean | UrlTree {
    const raw = this.cookieService.get('ddnsession');
    if (!raw) {
      return true;
    }
    let user: User;
    try {
      user = JSON.parse(raw) as User;
    } catch {
      return true;
    }
    if (!user?.token || user.signup_complete !== false) {
      return true;
    }

    const path = this.normalizePath(this.router.url);
    if (this.isExempt(path)) {
      return true;
    }

    const rolePath = this.signupPathForRole(user.role);
    return this.router.createUrlTree(['/' + content.WEBDDN_SIGNUP_PATH, rolePath]);
  }

  private normalizePath(url: string): string {
    const noQuery = url.split('?')[0];
    const noHash = noQuery.split('#')[0];
    return noHash || '/';
  }

  private isExempt(path: string): boolean {
    const signupBase = '/' + content.WEBDDN_SIGNUP_PATH;
    if (path === signupBase || path.startsWith(signupBase + '/')) {
      return true;
    }
    const loginPath = '/' + content.WEBDDN_LOGIN_PATH;
    if (path === loginPath || path.startsWith(loginPath + '/')) {
      return true;
    }
    if (path.startsWith('/forgot-password') || path.startsWith('/reset-password')) {
      return true;
    }
    if (path.startsWith('/signout') || path.startsWith('/logout')) {
      return true;
    }
    return false;
  }

  private signupPathForRole(role?: string): string {
    if (role === 't') return content.WEBDDN_TRANSPORTER || 'agent';
    if (role === 'b') return (content as any).WEBDDN_BUSINESS_USER || content.WEBDDN_PASSENGER || 'customer';
    if (role === 'm') return content.WEBDDN_MANAGER || 'manager';
    return content.WEBDDN_PASSENGER || 'customer';
  }
}

/**
 * Modern Functional Route Guard for Angular 22
 */
export const signupIncompleteGuard: CanActivateFn = () => {
  return inject(SignupIncompleteGuard).canActivate();
};
